import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  LogIn,
  LogOut,
  MapPin,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import "../../CSS/Global.css";
import "../Admission/CustomerStyle.css";
import EmptyStateBlock from "../EmptyStateBlock";
import { useSearch } from "../../SearchContext";
import { useFilter } from "../../FilterContext";
import Loader from "../Loader";
import Swal from "sweetalert2";
import { decryptToken } from "../utils/getToken";

function profileSlug(name) {
  const base = String(name || "profile").trim() || "profile";
  return encodeURIComponent(base.replace(/\s+/g, "-").slice(0, 80));
}

/** API / Sequelize may send staff_name, staffName, or omit; DB rows can have null. */
function attendanceStaffName(row) {
  if (!row || typeof row !== "object") return "";
  const n = row.staff_name ?? row.staffName ?? row.name;
  if (n == null) return "";
  const s = String(n).trim();
  if (!s || s === "undefined" || s === "null") return "";
  return s;
}

const TeamAttendance = () => {
  const { globalSearch } = useSearch();
  const { entriesPerPage } = useFilter();
  const [teamSearch, setTeamSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [load, setLoad] = useState(true);
  const [employees, setEmployees] = useState([]);
  const navigate = useNavigate();
  const apiUrl = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const canBulkDelete = useCallback(() => {
    if (!token) return false;
    try {
      const d = jwtDecode(token);
      const r = String(d.role ?? d.type ?? "").toLowerCase();
      return r === "admin" || r === "superadmin";
    } catch {
      return false;
    }
  }, [token]);

  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    if (!bulkSelectMode) setSelectedIds([]);
  }, [bulkSelectMode]);

  const toggleSelectId = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedIds([]);

  const safeDate = (dateTime) =>
    dateTime
      ? new Date(dateTime).toLocaleString("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "—";

  const fetchDataAttendance = useCallback(async () => {
    setLoad(true);
    try {
      const response = await fetch(`${apiUrl}/staffattendance`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const res = await response.json();
      setEmployees(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load attendance.");
      setEmployees([]);
    } finally {
      setLoad(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchDataAttendance();
  }, [fetchDataAttendance]);

  const q = (teamSearch.trim() || globalSearch.trim()).toLowerCase();
  const filteredEmployees = useMemo(() => {
    if (!q) return employees;
    return employees.filter((row) => {
      const hay = [
        attendanceStaffName(row),
        row.staff_id,
        row.checkin_location,
        row.checkout_location,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [employees, q]);

  useEffect(() => {
    setCurrentPage(1);
  }, [q, entriesPerPage]);

  const totalMatching = filteredEmployees.length;
  const totalPages = Math.max(1, Math.ceil(totalMatching / entriesPerPage) || 1);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredEmployees.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const pageLabelFrom = totalMatching === 0 ? 0 : indexOfFirst + 1;
  const pageLabelTo = Math.min(indexOfLast, totalMatching);
  const searchActive = Boolean(teamSearch.trim() || globalSearch.trim());

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const selectAllOnCurrentPage = () => {
    const pageIds = currentData
      .map((e) => e.id)
      .filter((x) => x != null)
      .map(Number)
      .filter(Number.isFinite);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleBulkDelete = async () => {
    if (!canBulkDelete() || selectedIds.length === 0) return;
    const first = await Swal.fire({
      title: `Delete ${selectedIds.length} attendance record(s)?`,
      html: `<p style="text-align:left;margin:0 0 8px">This permanently removes selected attendance rows for your account.</p><p style="text-align:left;margin:0;font-size:13px;color:#64748b">You cannot undo this action.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Delete",
      focusCancel: true,
    });
    if (!first.isConfirmed) return;

    setBulkDeleting(true);
    try {
      const res = await fetch(`${apiUrl}/staffattendance/bulk-delete`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.isError) {
        throw new Error(data.message || "Bulk delete failed");
      }
      const removed = new Set(selectedIds);
      setEmployees((prev) => (Array.isArray(prev) ? prev.filter((e) => !removed.has(Number(e.id))) : []));
      clearSelection();
      setBulkSelectMode(false);
      toast.success(data.message || `Removed ${data.deleted ?? selectedIds.length} record(s).`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e.message || "Bulk delete failed", "error");
    } finally {
      setBulkDeleting(false);
    }
  };

  const openProfile = (row) => {
    const sid = row.staff_id ?? row.id;
    const nm = attendanceStaffName(row);
    const slug = profileSlug(nm || `staff-${sid}`);
    navigate(`/employee-profile/${sid}/${slug}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this attendance record?",
      text: "This permanently removes the record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${apiUrl}/staffattendance/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.isError) {
        throw new Error(data.message || data.error || "Delete failed");
      }
      setEmployees((prev) => prev.filter((item) => item.id !== id));
      setSelectedIds((prev) => prev.filter((x) => x !== Number(id)));
      toast.success(data.message || "Record deleted.");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Delete failed.");
    }
  };

  if (load) {
    return <Loader />;
  }

  return (
    <>
      <div
        className={`admission-page${
          bulkSelectMode && canBulkDelete() && selectedIds.length > 0 ? " admission-page--bulk-dock" : ""
        }`}
      >
        <div className="admission-controls">
          <div className="customers-header admission-page__toolbar admission-page__toolbar--integrated">
            <div className="customer-dropdown admission-status-filter-anchor">
              <p className="dashboard-title">Team attendance</p>
            </div>
            <div className="customer-warning-new-hamburger-help">
              <div className="customer-new admission-toolbar-actions">
                {canBulkDelete() && (
                  <button
                    type="button"
                    className={`admission-bulk-mode-btn${bulkSelectMode ? " admission-bulk-mode-btn--active" : ""}`}
                    onClick={() => setBulkSelectMode((m) => !m)}
                    aria-pressed={bulkSelectMode}
                  >
                    {bulkSelectMode ? "Cancel select" : "Select"}
                  </button>
                )}
                <button type="button" className="oms-add-btn" onClick={() => fetchDataAttendance()}>
                  <i className="fa-solid fa-rotate-right" aria-hidden /> Refresh
                </button>
              </div>
            </div>
          </div>

          {bulkSelectMode && canBulkDelete() && (
            <div className="admission-bulk-strip admission-bulk-strip--embedded" role="region" aria-label="Bulk selection">
              <div className="admission-bulk-strip__inner">
                <span className="admission-bulk-strip__count">
                  <strong>{selectedIds.length}</strong> selected
                  {totalMatching > 0 && (
                    <span className="admission-bulk-strip__hint"> · across pages when you navigate</span>
                  )}
                </span>
                <div className="admission-bulk-strip__btns">
                  <button type="button" className="admission-bulk-strip__linkish" onClick={selectAllOnCurrentPage}>
                    All on this page
                  </button>
                  <button
                    type="button"
                    className="admission-bulk-strip__linkish"
                    onClick={clearSelection}
                    disabled={selectedIds.length === 0}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="admission-controls__search">
            <div className="admission-search-summary__search">
              <Search className="admission-search-summary__icon" size={17} strokeWidth={2.25} aria-hidden />
              <input
                type="search"
                className="admission-search-summary__input"
                placeholder="Search name, staff ID, check-in / check-out location…"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                aria-label="Search attendance"
              />
              {teamSearch.trim() !== "" && (
                <button
                  type="button"
                  className="admission-search-summary__clear admission-search-summary__clear--icon"
                  onClick={() => setTeamSearch("")}
                  aria-label="Clear search"
                >
                  <X size={18} strokeWidth={2.25} aria-hidden />
                </button>
              )}
            </div>
            <div className="admission-search-summary__stats" role="status">
              <span className="admission-search-summary__pill" title="Total rows loaded">
                <strong>{employees.length}</strong> record{employees.length === 1 ? "" : "s"}
              </span>
              {searchActive && (
                <span className="admission-search-summary__pill admission-search-summary__pill--accent">
                  <strong>{totalMatching}</strong> match{totalMatching === 1 ? "" : "es"}
                </span>
              )}
              {totalMatching > 0 && (
                <span className="admission-search-summary__range">
                  Rows {pageLabelFrom}–{pageLabelTo} of {totalMatching}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="admission-cards-grid">
          {totalMatching === 0 && employees.length > 0 ? (
            <EmptyStateBlock
              Icon={ClipboardList}
              title="No attendance matches your search"
              description="Try different keywords or clear the search box."
              className="admission-empty--wide admission-empty-center max-w-full"
            />
          ) : currentData.length > 0 ? (
            currentData.map((row, index) => (
              <div
                className={`ecom-customer-card${
                  bulkSelectMode && canBulkDelete() ? " ecom-customer-card--selectable" : ""
                }`}
                key={row.id ?? index}
                onClick={() => {
                  if (bulkSelectMode && canBulkDelete()) return;
                  openProfile(row);
                }}
                style={{ cursor: bulkSelectMode && canBulkDelete() ? "default" : "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (bulkSelectMode && canBulkDelete()) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openProfile(row);
                  }
                }}
              >
                {bulkSelectMode && canBulkDelete() && (
                  <label
                    className="admission-card-checkbox-wrap"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="admission-card-checkbox-input"
                      checked={selectedIds.includes(Number(row.id))}
                      onChange={() => toggleSelectId(row.id)}
                      aria-label={`Select attendance ${attendanceStaffName(row) || row.staff_id || row.id}`}
                    />
                    <span className="admission-card-checkbox-face" aria-hidden />
                  </label>
                )}
                <div className="admission-card__hero">
                  <div className="admission-card__hero-top">
                    {row.selfie ? (
                      <div className="admission-card__avatar">
                        <img
                          src={row.selfie.startsWith("http") ? row.selfie : `${apiUrl}/${row.selfie}`}
                          alt=""
                        />
                      </div>
                    ) : (
                      <div className="admission-card__avatar admission-card__avatar--placeholder" aria-hidden>
                        <User size={26} strokeWidth={2} />
                      </div>
                    )}
                    <div className="admission-card__intro">
                      <h2 className="ecom-customer-name admission-card__name">
                        {attendanceStaffName(row) || `Staff (${row.staff_id ?? "—"})`}
                      </h2>
                      {row.staff_id != null && row.staff_id !== "" && (
                        <div className="admission-card__sub" title="Staff ID">
                          {String(row.staff_id)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admission-card__control-row">
                    <span className="admission-card__control-spacer" aria-hidden />
                    <div
                      className="ecom-customer-actions admission-card__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="ecom-delete-btn"
                        type="button"
                        title="Delete record"
                        aria-label="Delete attendance row"
                        onClick={() => handleDelete(row.id)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="admission-card__body">
                  <div className="ecom-customer-row admission-card__row admission-card__row--contact">
                    <LogIn className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                    <span>
                      {row.checkin_date ? (
                        <>
                          <strong>Check in</strong>
                          {" · "}
                          {safeDate(row.checkin_date)}
                          {row.checkin_location && (
                            <span className="block mt-1 opacity-90">
                              <MapPin className="inline align-text-bottom mr-1" size={14} />
                              {row.checkin_location}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="opacity-75">No check-in</span>
                      )}
                    </span>
                  </div>
                  <div className="ecom-customer-row admission-card__row">
                    <LogOut className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                    <span>
                      {row.checkout_date ? (
                        <>
                          <strong>Check out</strong>
                          {" · "}
                          {safeDate(row.checkout_date)}
                          {row.checkout_location && (
                            <span className="block mt-1 opacity-90">
                              <MapPin className="inline align-text-bottom mr-1" size={14} />
                              {row.checkout_location}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="opacity-75">No check-out</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <EmptyStateBlock
              Icon={ClipboardList}
              title="No attendance records yet"
              description="Records appear here after staff check in. Use Refresh to reload."
              className="admission-empty--wide admission-empty-center max-w-full"
            />
          )}
        </div>

        {bulkSelectMode && canBulkDelete() && selectedIds.length > 0 && (
          <div className="admission-bulk-dock" role="toolbar" aria-label="Bulk delete">
            <div className="admission-bulk-dock__panel">
              <div className="admission-bulk-dock__text">
                <span className="admission-bulk-dock__n">{selectedIds.length}</span>
                <span>record{selectedIds.length === 1 ? "" : "s"} marked</span>
              </div>
              <div className="admission-bulk-dock__actions">
                <button type="button" className="admission-bulk-dock__secondary" onClick={clearSelection} disabled={bulkDeleting}>
                  Clear
                </button>
                <button type="button" className="admission-bulk-dock__danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                  <Trash2 size={17} strokeWidth={2.25} aria-hidden />
                  {bulkDeleting ? "Deleting…" : "Delete selected"}
                </button>
              </div>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination admission-pagination">
            <button
              type="button"
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronLeft size={18} />
            </button>
            <button type="button" className="pagination-btn active">
              {currentPage}
            </button>
            <button
              type="button"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronRight size={18} />
            </button>
            <button
              type="button"
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default TeamAttendance;
